import type { RegisteredComponent } from "@builder.io/sdk-react";
import dynamic from "next/dynamic";

import { IMAGE_FILE_TYPES } from "@/libs/builder/consts";
import { getBuilderModelName } from "@/libs/builder/tools/getBuilderModelName";
import { withValidation } from "@/libs/builder/validators/validateBuilderComponentData";
import { fullRichtextControls } from "@/types/richtext-controls";

import { withTriggerCompatibilityValidation } from "./withTriggerCompatibilityValidation";

export const onsitePersonalizationInputs = [
  {
    name: "triggerMode",
    friendlyName: "Trigger mode",
    type: "string",
    enum: ["and", "or"],
    defaultValue: "or",
    required: true,
    helperText:
      '"and" — all enabled triggers must fire; "or" — any single trigger is enough. Use "and" only when 2+ triggers are enabled.',
  },
  {
    name: "isInactivityModalEnabled",
    type: "boolean",
    required: true,
    defaultValue: false,
  },
  {
    name: "isQuickSwipeUpModalEnabled",
    type: "boolean",
    required: true,
    defaultValue: false,
  },
  {
    name: "isScrollDepthModalEnabled",
    type: "boolean",
    required: true,
    defaultValue: false,
  },
  {
    name: "isExitIntentEnabled",
    type: "boolean",
    required: true,
    defaultValue: false,
  },
  {
    name: "isTimeOnPageEnabled",
    type: "boolean",
    required: true,
    defaultValue: false,
  },
  {
    name: "title",
    type: "string",
    defaultValue: "Exclusive content",
    required: true,
  },
  {
    name: "popupId",
    type: "string",
    friendlyName: "Popup Identifier for multiple popups support",
  },
  {
    name: "description",
    type: "string",
    defaultValue:
      "This article is available only to Ploom Club members, to access it and benefit from earning points, please sign in to your Ploom account now.",
  },
  {
    name: "mainImage",
    required: true,
    type: "file",
    defaultValue: "",
    allowedFileTypes: IMAGE_FILE_TYPES,
  },
  {
    name: "mobileImage",
    type: "file",
    defaultValue: "",
    allowedFileTypes: IMAGE_FILE_TYPES,
  },
  {
    name: "imageAltText",
    type: "string",
    required: true,
    defaultValue: "alt text",
  },
  {
    name: "primaryButtonText",
    type: "string",
    required: true,
    defaultValue: "Go to checkout",
  },
  {
    name: "primaryButtonUrl",
    friendlyName: "Primary button URL (overrides default checkout URL)",
    type: "url",
  },
  {
    name: "secondaryButtonText",
    type: "string",
    defaultValue: "Continue shopping",
  },
  {
    name: "secondaryButtonUrl",
    friendlyName: "Secondary button URL (overrides default close behaviour)",
    type: "url",
  },
  {
    name: "isPurchasedDaysAgoModalEnabled",
    friendlyName: "Enable purchased X days ago modal",
    helperText:
      "Show modal only if the user has made a purchase at least X days ago (configured with purchasedDaysAgoDelay)",
    type: "boolean",
    required: true,
    defaultValue: false,
  },
  {
    name: "purchasedDaysAgoDelay",
    friendlyName: "Set purchased X days ago delay in days",
    helperText: "Number of days to check for recent purchases before showing the modal",
    type: "number",
    defaultValue: 7,
    showIf: `options.get("isPurchasedDaysAgoModalEnabled") === true`,
  },
  {
    name: "minCartItemCount",
    friendlyName: "Min. cart items to show popup",
    type: "number",
    defaultValue: 1,
  },
  {
    name: "isImageOnLeft",
    type: "boolean",
    defaultValue: true,
  },
  {
    name: "inactivityDelay",
    friendlyName: "Inactivity delay, sec",
    type: "string",
    enum: ["10", "20", "30", "40", "50", "60"],
    defaultValue: "20",
    required: true,
  },
  {
    name: "timeOnPageDelay",
    friendlyName: "Time on page (site) delay, sec",
    type: "number",
    defaultValue: "5",
    required: true,
  },
  {
    name: "frequencyType",
    friendlyName: "Frequency — how often to show",
    helperText:
      "once = never show again after first display. everyVisit = show on every new browser session. everyXDays = re-show after the configured number of days (resets at start of day). Default: once.",
    type: "string",
    enum: ["once", "everyVisit", "everyXDays"],
    defaultValue: "once",
    required: true,
  },
  {
    name: "frequencyValue",
    friendlyName: "Frequency value (days)",
    helperText:
      "Number of days before the pop-up can appear again (used with everyXDays). Resets at the start of each day.",
    type: "number",
    showIf: 'options.get("frequencyType") === "everyXDays"',
  },
  {
    name: "hideModalExpireTime",
    friendlyName: "[Deprecated] Hide modal expire time, days",
    helperText: "Deprecated. Use frequencyType + frequencyValue instead.",
    type: "string",
    enum: ["1", "2", "3", "4", "5", "6", "7"],
    hideFromUI: true,
  },
  {
    name: "isUserActionTriggerEnabled",
    friendlyName: "Enable User Action trigger",
    type: "boolean",
    defaultValue: false,
  },
  {
    name: "userActionTrigger",
    friendlyName: "User Action",
    helperText:
      "Open the pop-up when the user performs the selected action. 'completeRegistration' is intended for Frequency = once.",
    type: "string",
    enum: ["none", "addToCart", "removeFromCart", "placeOrder", "completeRegistration", "loginSuccess"],
    defaultValue: "none",
    showIf: 'options.get("isUserActionTriggerEnabled") === true',
  },
  {
    name: "isLocalTimeTriggerEnabled",
    friendlyName: "Enable Local Time condition",
    type: "boolean",
    defaultValue: false,
  },
  {
    name: "localTimeWindow",
    friendlyName: "Local Time window",
    helperText:
      "Presets — morning: 06:00–11:59, afternoon: 12:00–17:59, night: 18:00–05:59 (wraps). Choose 'custom' for per-popup hours.",
    type: "string",
    enum: ["morning", "afternoon", "night", "custom"],
    defaultValue: "morning",
    showIf: 'options.get("isLocalTimeTriggerEnabled") === true',
  },
  {
    name: "customStartHour",
    friendlyName: "Custom start hour (0–23)",
    helperText: "User local time. Inclusive.",
    type: "number",
    showIf: 'options.get("isLocalTimeTriggerEnabled") === true && options.get("localTimeWindow") === "custom"',
  },
  {
    name: "customEndHour",
    friendlyName: "Custom end hour (0–23)",
    helperText: "User local time. Exclusive. End ≤ start wraps past midnight.",
    type: "number",
    showIf: 'options.get("isLocalTimeTriggerEnabled") === true && options.get("localTimeWindow") === "custom"',
  },
  {
    name: "paragraph",
    type: "richTextEditor",
    options: {
      controls: fullRichtextControls,
    },
  },
  {
    name: "couponContent",
    type: "object",
    required: true,
    defaultValue: {
      isCouponAvailable: false,
      coupon: "ABC123",
      copyCouponLabel: "Copy",
      couponHelperText: "Copy this code and paste it in the  promo code field at checkout",
    },
    subFields: [
      {
        name: "isCouponAvailable",
        type: "boolean",
      },
      {
        name: "coupon",
        type: "string",
      },
      {
        name: "copyCouponLabel",
        type: "string",
      },
      {
        name: "couponHelperText",
        type: "string",
      },
    ],
  },
] as const satisfies Inputs;

export const OnsitePersonalizationModalComponent: RegisteredComponent = {
  name: "Onsite Personalization Modal",
  component: dynamic(
    async () => {
      const { OnsitePersonalizationModal } = await import("./OnsitePersonalizationModal");
      return withTriggerCompatibilityValidation(
        withValidation(onsitePersonalizationInputs, OnsitePersonalizationModal, "OnsitePersonalizationModal")
      );
    },
    { ssr: false }
  ),
  models: [getBuilderModelName("onsite-personalization-modal")],
  inputs: onsitePersonalizationInputs,
  canHaveChildren: true,
  defaultChildren: [],
};
